/**
 * The operating company, in one place.
 *
 * WHY THIS PACKAGE EXISTS: the same entity block (name, address, register,
 * Geschäftsführer) is legally required on both websites — ÖPM's Impressum,
 * AGB, Datenschutz, Widerrufsbelehrung and Kontakt pages, the generated
 * Kaufvertrag and Rechnung, every footer, and the landvest.eu equivalents.
 * It was copy-pasted into ~13 places, which meant the 2026-09 cutover from
 * the predecessor entity to LandVest GmbH had to be done thirteen times,
 * and a single miss would have published a stale legal entity.
 *
 * Change the company details HERE and every surface follows — with one
 * manual step: the two GoTrue-delivered e-mail templates are resolved at
 * MJML build time and must be rebuilt AND re-pasted into the Supabase Auth
 * dashboard (emails/templates/_partials/CATALOG.md). CI fails until the
 * rebuild happens; the paste is on a human.
 *
 * Source-exported (no build step) exactly like @oekopunktemarkt/report-contract,
 * so both Next apps — and the MDX legal pages, which can import TS — consume
 * it without any dist coupling.
 *
 * REACH: every surface is fed from here, but by three different mechanisms —
 *   1. direct import (TSX pages, footers, e-mail footer builder, offer text);
 *   2. `{{platform_*}}` tokens in the Kaufvertrag/Rechnung templates, filled
 *      by their renderers at generation time;
 *   3. `{{PLATFORM_*}}` tokens in the MJML e-mail footer partials, filled by
 *      the send-time loader — except the two GoTrue-delivered `supabase-*`
 *      templates, which never pass through the loader and are resolved at
 *      MJML build time (emails/scripts/build-all.js imports this package).
 * Guards: apps/website's legal-entity-footer.guard.test.ts pins both e-mail
 * delivery paths; scripts/check-legal-entity-sweep.mjs (own CI step) fails
 * the build if the predecessor entity resurfaces in any customer-facing
 * source repo-wide.
 */

export interface LegalEntity {
  /** Registered company name, as it must appear in the Impressum. */
  readonly name: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly country: string;
  /** Handelsregister number, e.g. "HRB 314094". */
  readonly register: string;
  /** Registergericht in the NOMINATIVE, e.g. "Amtsgericht München" — for
   *  "Registergericht: …" labels. */
  readonly registerCourt: string;
  /**
   * Registergericht in the GENITIVE, e.g. "Amtsgerichts München".
   *
   * German legal prose says "eingetragen im Handelsregister DES
   * AmtsgerichtS München" — dropping the genitive -s is a grammar error that
   * shipped into a contract once already. Held as its own string rather than
   * derived, because the inflection depends on the court's name.
   */
  readonly registerCourtGenitive: string;
  /**
   * Geschäftsführer, IN THE ORDER THEY SHOULD BE PRINTED. Order is a
   * deliberate business choice (requested 2026-09-02), not alphabetical —
   * do not sort this.
   */
  readonly managingDirectors: readonly string[];
  /**
   * USt-IdNr., or null while none is issued.
   *
   * DELIBERATELY null: LandVest GmbH's own number has not been issued yet.
   * The previous value (DE332654605) belongs to AMWP Beratungsgesellschaft
   * mbH — a DIFFERENT legal entity — and must never be published under this
   * company's name. Consumers must omit the whole USt-IdNr. section while
   * this is null rather than printing a placeholder.
   */
  readonly vatId: string | null;
}

export const LEGAL_ENTITY: LegalEntity = {
  name: "LandVest GmbH",
  street: "Kreuzwiesenstraße 13",
  postalCode: "82065",
  city: "Baierbrunn",
  country: "Deutschland",
  register: "HRB 314094",
  registerCourt: "Amtsgericht München",
  registerCourtGenitive: "Amtsgerichts München",
  managingDirectors: ["Anja Ziolko", "Thomas Schmidt"],
  vatId: null,
};

/** "82065 Baierbrunn" */
export function entityPostalCity(entity: LegalEntity = LEGAL_ENTITY): string {
  return `${entity.postalCode} ${entity.city}`;
}

/** "Kreuzwiesenstraße 13, 82065 Baierbrunn, Deutschland" */
export function entityAddress(entity: LegalEntity = LEGAL_ENTITY): string {
  return `${entity.street}, ${entityPostalCity(entity)}, ${entity.country}`;
}

/** "LandVest GmbH, Kreuzwiesenstraße 13, 82065 Baierbrunn, Deutschland" */
export function entityNameAndAddress(entity: LegalEntity = LEGAL_ENTITY): string {
  return `${entity.name}, ${entityAddress(entity)}`;
}

/**
 * "Anja Ziolko und Thomas Schmidt" — German prose conjunction, for the
 * "Vertreten durch die Geschäftsführer" line. Order comes from the data.
 */
export function managingDirectorsText(entity: LegalEntity = LEGAL_ENTITY): string {
  const names = [...entity.managingDirectors];
  if (names.length === 0) {
    // A GmbH always has at least one Geschäftsführer; rendering an empty
    // "Vertreten durch die Geschäftsführer:" line would be a silent § 5 DDG
    // breach, so this is a hard error, not an empty string.
    throw new Error("LEGAL_ENTITY.managingDirectors must not be empty");
  }
  if (names.length === 1) return names[0];
  const last = names.pop() as string;
  return `${names.join(", ")} und ${last}`;
}

/** "HRB 314094 · Registergericht: Amtsgericht München" */
export function entityRegisterLine(entity: LegalEntity = LEGAL_ENTITY): string {
  return `${entity.register} · Registergericht: ${entity.registerCourt}`;
}

/**
 * The company's public LinkedIn pages — stated ONCE here. Consumed by the
 * e-mail signature builder (emails/scripts/build-signatures.js), the ÖPM
 * footer, the ÖPM tenant config (Organization.sameAs) and LandVest's
 * Organization schema. Both pages exist: the Ökopunktemarkt showcase page and
 * the LandVest GmbH company page (created 2026-09-03). If the showcase is
 * recreated under the company page, change it here and rebuild the
 * signatures. No UTM parameters: LinkedIn strips query strings.
 */
export const LINKEDIN_PAGES = Object.freeze({
  landvest: "https://www.linkedin.com/company/landvest-gmbh/",
  oekopunktemarkt: "https://www.linkedin.com/showcase/%C3%B6kopunktemarkt/",
});

/**
 * The `{{PLATFORM_*}}` placeholder dictionary for the MJML e-mail footer
 * partials. Defined ONCE here; consumed by the send-time loader
 * (apps/website template-loader.ts) and by the MJML build script for the
 * GoTrue-delivered templates (emails/scripts/build-all.js), so the two
 * delivery paths cannot drift apart.
 */
export const PLATFORM_EMAIL_TOKENS: Readonly<Record<string, string>> = Object.freeze({
  PLATFORM_NAME: LEGAL_ENTITY.name,
  PLATFORM_STREET: LEGAL_ENTITY.street,
  PLATFORM_POSTAL_CITY: entityPostalCity(),
  PLATFORM_CITY: LEGAL_ENTITY.city,
  PLATFORM_COUNTRY: LEGAL_ENTITY.country,
  PLATFORM_REGISTER: LEGAL_ENTITY.register,
  PLATFORM_REGISTER_COURT: LEGAL_ENTITY.registerCourt,
  PLATFORM_MANAGING_DIRECTORS: managingDirectorsText(),
});

/**
 * The `{{platform_*}}` token map for the generated Kaufvertrag and Rechnung
 * templates (apps/website/src/templates/*.md) — one source for both
 * renderers.
 */
export function platformDocumentTokens(entity: LegalEntity = LEGAL_ENTITY): Record<string, string> {
  return {
    platform_name: entity.name,
    platform_street: entity.street,
    platform_postal_city: entityPostalCity(entity),
    platform_country: entity.country,
    platform_register: entity.register,
    platform_register_court: entity.registerCourt,
    platform_register_line: entityRegisterLine(entity),
    platform_managing_directors: managingDirectorsText(entity),
  };
}
